import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Calculator, TrendingUp, DollarSign, Zap, ArrowRight, Download, CheckCircle, Mail, Loader2 } from 'lucide-react';
import { PageHero, Section, SectionTitle } from '@/components/shared/Section';
import { fadeUp } from '@/lib/motion';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CalculadoraROIPage() {
  const [formData, setFormData] = useState({
    industria: 'retail',
    sucursales: '',
    ventasMensuales: '',
    dispositivos: '',
    solucion: 'autoservicio',
  });
  const [resultado, setResultado] = useState(null);
  const [sendForm, setSendForm] = useState({ name: '', email: '', company: '', phone: '' });
  const [sending, setSending] = useState(false);

  const handleDownloadPDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const industrias = {retail:'Retail / Supermercados', horeca:'Gastronomía / HORECA', banca:'Banca / Servicios', salud:'Salud / Clínicas', otros:'Otros'};
    const soluciones = {autoservicio:'Kioscos Autoservicio', esl:'ESL / Precios Digitales', balanzas:'Balanzas con IA', pos:'Puntos de Venta'};
    const fecha = new Date().toLocaleDateString('es-CL');
    const hora = new Date().toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' });
    const fmt = (n) => '$' + new Intl.NumberFormat('es-CL').format(Math.round(n));
    const fmtM = (n) => '$' + (n / 1000000).toFixed(1) + 'M';

    // ============ HEADER ============
    doc.setFillColor(10, 37, 64); // Navy ALLCOM
    doc.rect(0, 0, pageWidth, 38, 'F');
    // Accent bar
    doc.setFillColor(248, 217, 15); // Yellow accent
    doc.rect(0, 38, pageWidth, 2, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(22);
    doc.text('ALLCOM', 15, 20);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(148, 163, 184);
    doc.text('Retail Tech · IA · Operación 24/7', 15, 27);
    // Right-side tag
    doc.setFillColor(14, 165, 233);
    doc.roundedRect(pageWidth - 60, 12, 45, 14, 2, 2, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.text('ANÁLISIS ROI', pageWidth - 55, 21);

    // ============ TÍTULO ============
    doc.setTextColor(10, 37, 64);
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('Proyección de Retorno de Inversión', 15, 55);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 116, 139);
    doc.text(`Generado el ${fecha} a las ${hora} · Válido por 30 días`, 15, 62);

    // ============ TABLA DATOS DE OPERACIÓN ============
    autoTable(doc, {
      startY: 72,
      head: [['Datos de tu Operación', '']],
      body: [
        ['Industria', industrias[formData.industria] || '—'],
        ['Sucursales', `${resultado.sucursales}`],
        ['Equipos a implementar', `${resultado.dispositivos}`],
        ['Tipo de solución', soluciones[formData.solucion] || '—'],
        ['Ventas mensuales declaradas', formData.ventasMensuales ? fmt(parseFloat(formData.ventasMensuales)) : '—'],
      ],
      theme: 'grid',
      headStyles: { fillColor: [10, 37, 64], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 11, halign: 'left' },
      bodyStyles: { fontSize: 10, textColor: [51, 65, 85] },
      columnStyles: { 0: { fontStyle: 'bold', textColor: [10, 37, 64], cellWidth: 75 }, 1: { textColor: [51, 65, 85] } },
      margin: { left: 15, right: 15 },
    });

    // ============ MÉTRICAS CLAVE — 4 BOXES ============
    let y = doc.lastAutoTable.finalY + 10;
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(10, 37, 64);
    doc.text('Métricas Clave', 15, y);
    y += 6;

    const bw = (pageWidth - 30 - 9) / 4; // 4 boxes, 3px gaps
    const boxes = [
      { label: 'INVERSIÓN', value: fmtM(resultado.inversion), bg: [248, 250, 252], border: [203, 213, 225], textColor: [10, 37, 64], valueColor: [10, 37, 64] },
      { label: 'AHORRO ANUAL', value: fmtM(resultado.ahorroAnual), bg: [240, 253, 244], border: [134, 239, 172], textColor: [22, 101, 52], valueColor: [22, 163, 74] },
      { label: 'ROI PROYECTADO', value: `${resultado.roi}%`, bg: [14, 165, 233], border: [14, 165, 233], textColor: [255, 255, 255], valueColor: [255, 255, 255] },
      { label: 'PAYBACK', value: `${resultado.paybackMeses} meses`, bg: [254, 249, 195], border: [253, 224, 71], textColor: [120, 84, 0], valueColor: [120, 84, 0] },
    ];
    boxes.forEach((b, i) => {
      const x = 15 + i * (bw + 3);
      doc.setFillColor(...b.bg);
      doc.setDrawColor(...b.border);
      doc.setLineWidth(0.3);
      doc.roundedRect(x, y, bw, 26, 2, 2, 'FD');
      doc.setFontSize(7);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...b.textColor);
      doc.text(b.label, x + 3, y + 7);
      doc.setFontSize(15);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...b.valueColor);
      doc.text(b.value, x + 3, y + 20);
    });
    y += 32;

    // ============ PROYECCIÓN 36 MESES ============
    const a1 = resultado.ahorroAnual - resultado.inversion;
    const a2 = resultado.ahorroAnual;
    const a3 = resultado.ahorroAnual;
    const acum1 = a1;
    const acum2 = a1 + a2;
    const acum3 = a1 + a2 + a3;
    autoTable(doc, {
      startY: y,
      head: [['Año', 'Ahorro', 'Inversión', 'Flujo neto', 'Acumulado']],
      body: [
        ['Año 1', fmtM(resultado.ahorroAnual), `-${fmtM(resultado.inversion)}`, fmtM(a1), fmtM(acum1)],
        ['Año 2', fmtM(resultado.ahorroAnual), '—', fmtM(a2), fmtM(acum2)],
        ['Año 3', fmtM(resultado.ahorroAnual), '—', fmtM(a3), fmtM(acum3)],
      ],
      foot: [['Beneficio acumulado 36 meses', '', '', '', fmtM(acum3)]],
      theme: 'striped',
      headStyles: { fillColor: [10, 37, 64], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 10 },
      bodyStyles: { fontSize: 10, textColor: [51, 65, 85] },
      footStyles: { fillColor: [240, 253, 244], textColor: [22, 101, 52], fontStyle: 'bold', fontSize: 11 },
      columnStyles: { 0: { fontStyle: 'bold' }, 4: { fontStyle: 'bold', textColor: [22, 163, 74] } },
      margin: { left: 15, right: 15 },
    });

    y = doc.lastAutoTable.finalY + 8;

    // ============ POR QUÉ ALLCOM ============
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(10, 37, 64);
    doc.text('¿Por qué ALLCOM?', 15, y);
    y += 5;
    autoTable(doc, {
      startY: y,
      body: [
        ['Trayectoria', '21+ años en retail-tech Chile'],
        ['Cobertura', '1.900 dispositivos activos en 9 ciudades'],
        ['Soporte', 'SLA 24/7 garantizado + repuestos nacionales'],
        ['Partners', 'SOLUM, Wintec, Epson, Lenovo, Zebra, Advantech'],
        ['Diferencial', 'Hardware + Software + IA + Operación, un solo proveedor'],
      ],
      theme: 'plain',
      bodyStyles: { fontSize: 9, textColor: [51, 65, 85] },
      columnStyles: { 0: { fontStyle: 'bold', textColor: [14, 165, 233], cellWidth: 40 } },
      margin: { left: 15, right: 15 },
    });

    y = doc.lastAutoTable.finalY + 5;

    // Disclaimer
    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(148, 163, 184);
    const disc = 'Estimaciones basadas en proyectos similares ejecutados por ALLCOM. Los valores reales pueden variar según volumen, layout de tienda y complejidad operativa. Análisis detallado gratuito en reunión con nuestro equipo.';
    doc.text(doc.splitTextToSize(disc, pageWidth - 30), 15, y);

    // ============ FOOTER ============
    const footerY = pageHeight - 22;
    doc.setFillColor(10, 37, 64);
    doc.rect(0, footerY, pageWidth, 22, 'F');
    doc.setFillColor(248, 217, 15);
    doc.rect(0, footerY, pageWidth, 1.5, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text('ALLCOM S.A.', 15, footerY + 8);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(148, 163, 184);
    doc.text('Andrés Bello 1567, Providencia · Santiago de Chile', 15, footerY + 13);
    doc.text('www.allcom.cl', 15, footerY + 18);
    // Right side contact
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text('Conversemos sobre tu proyecto', pageWidth - 15, footerY + 8, { align: 'right' });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(248, 217, 15);
    doc.text('ventas@allcom.cl · +56 9 7529 4340', pageWidth - 15, footerY + 13, { align: 'right' });
    doc.setTextColor(148, 163, 184);
    doc.text('Agenda reunión: allcom.cl/contacto', pageWidth - 15, footerY + 18, { align: 'right' });

    doc.save(`ALLCOM_Analisis_ROI_${fecha.replace(/\//g, '-')}.pdf`);
  };

  const handleEmailPDF = async (e) => {
    e.preventDefault();
    if (!sendForm.name.trim() || !sendForm.email.trim()) {
      toast.error('Nombre y email son requeridos');
      return;
    }
    setSending(true);
    try {
      // Build the PDF in-memory (without saving)
      // We temporarily patch jsPDF.save to capture the doc object — simpler: rebuild via same function but output base64
      const industrias = {retail:'Retail / Supermercados', horeca:'Gastronomía / HORECA', banca:'Banca / Servicios', salud:'Salud / Clínicas', otros:'Otros'};
      const soluciones = {autoservicio:'Kioscos Autoservicio', esl:'ESL / Precios Digitales', balanzas:'Balanzas con IA', pos:'Puntos de Venta'};
      const fecha = new Date().toLocaleDateString('es-CL');
      const hora = new Date().toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' });
      const fmt = (n) => '$' + new Intl.NumberFormat('es-CL').format(Math.round(n));
      const fmtM = (n) => '$' + (n / 1000000).toFixed(1) + 'M';
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      doc.setFillColor(10, 37, 64); doc.rect(0, 0, pageWidth, 38, 'F');
      doc.setFillColor(248, 217, 15); doc.rect(0, 38, pageWidth, 2, 'F');
      doc.setTextColor(255, 255, 255); doc.setFont('helvetica', 'bold'); doc.setFontSize(22); doc.text('ALLCOM', 15, 20);
      doc.setFontSize(9); doc.setFont('helvetica', 'normal'); doc.setTextColor(148, 163, 184); doc.text('Retail Tech · IA · Operación 24/7', 15, 27);
      doc.setFillColor(14, 165, 233); doc.roundedRect(pageWidth - 60, 12, 45, 14, 2, 2, 'F');
      doc.setTextColor(255, 255, 255); doc.setFontSize(8); doc.setFont('helvetica', 'bold'); doc.text('ANÁLISIS ROI', pageWidth - 55, 21);
      doc.setTextColor(10, 37, 64); doc.setFontSize(20); doc.setFont('helvetica', 'bold'); doc.text('Proyección de Retorno de Inversión', 15, 55);
      doc.setFontSize(9); doc.setFont('helvetica', 'normal'); doc.setTextColor(100, 116, 139); doc.text(`Generado el ${fecha} a las ${hora} · Válido por 30 días`, 15, 62);
      autoTable(doc, { startY: 70, head: [['PARÁMETRO', 'VALOR']], body: [
        ['Industria', industrias[formData.industria] || formData.industria],
        ['Solución', soluciones[formData.solucion] || formData.solucion],
        ['Sucursales', `${resultado.sucursales}`],
        ['Equipos a implementar', `${resultado.dispositivos}`],
      ], theme: 'striped', headStyles: { fillColor: [10, 37, 64], textColor: 255, fontStyle: 'bold', fontSize: 9 }, bodyStyles: { fontSize: 10, textColor: [30, 41, 59] }, alternateRowStyles: { fillColor: [248, 250, 252] }, margin: { left: 15, right: 15 } });
      const base64 = doc.output('datauristring').split(',')[1];
      const ahorroM = fmtM(resultado.ahorroAnual);
      await axios.post(`${API}/roi/send`, {
        name: sendForm.name,
        email: sendForm.email,
        company: sendForm.company,
        phone: sendForm.phone,
        pdf_base64: base64,
        roi_meta: {
          solucion: soluciones[formData.solucion] || formData.solucion,
          industria: industrias[formData.industria] || formData.industria,
          roi: resultado.roi,
          paybackMeses: resultado.paybackMeses,
          ahorroAnualM: ahorroM,
          inversion: resultado.inversion,
        },
      });
      toast.success('¡PDF enviado! Revisa tu correo en los próximos minutos.');
      setSendForm({ name: '', email: '', company: '', phone: '' });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al enviar. Intenta nuevamente.');
    } finally {
      setSending(false);
    }
  };

  const handleCalculate = () => {
    const sucursales = parseInt(formData.sucursales) || 1;
    const dispositivos = parseInt(formData.dispositivos) || 1;
    const ventas = parseFloat(formData.ventasMensuales) || 0;

    let inversion = 0;
    let ahorroAnual = 0;
    let descripcion = '';

    // Cálculos según tipo de solución
    if (formData.solucion === 'autoservicio') {
      inversion = dispositivos * 4500000; // $4.5M por kiosco
      ahorroAnual = dispositivos * 18000000; // $18M ahorro anual por kiosco
      descripcion = 'Reducción de personal + aumento de ventas por menor abandono';
    } else if (formData.solucion === 'esl') {
      const skus = dispositivos * 100; // Asumimos 100 SKUs por sucursal
      inversion = skus * 90000; // $90K por ESL
      ahorroAnual = skus * 12000; // $12K ahorro anual por SKU (errores + tiempo)
      descripcion = 'Eliminación de errores de precio + tiempo de actualización manual';
    } else if (formData.solucion === 'balanzas') {
      inversion = dispositivos * 2800000; // $2.8M por balanza IA
      ahorroAnual = (ventas * 12) * 0.025; // 2.5% reducción merma sobre ventas anuales
      descripcion = 'Reducción de merma + precisión en pesaje';
    } else if (formData.solucion === 'pos') {
      inversion = dispositivos * 1800000; // $1.8M por POS
      ahorroAnual = dispositivos * 8000000; // $8M ahorro por POS (vs PC común)
      descripcion = 'Mayor uptime + reducción mantenimiento vs PCs comunes';
    }

    const roi = ((ahorroAnual / inversion) * 100).toFixed(1);
    const paybackMeses = Math.ceil((inversion / ahorroAnual) * 12);

    setResultado({
      inversion,
      ahorroAnual,
      roi,
      paybackMeses,
      descripcion,
      sucursales,
      dispositivos,
    });
  };

  return (
    <div>
      <PageHero
        badge="HERRAMIENTA IA"
        badgeIcon={Calculator}
        h1="Calculadora de ROI"
        subtitle="Calcula el retorno de inversión de tu proyecto en menos de 2 minutos. Proyecciones basadas en 469 proyectos ejecutados."
      />

      <Section>
        <div className="max-w-4xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Left: Form */}
            <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5 }}>
              <div className="p-8 rounded-2xl bg-white border border-slate-200 shadow-lg">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#0EA5E9] to-[#0191B4] flex items-center justify-center">
                    <Calculator className="w-6 h-6 text-white" strokeWidth={1.5} />
                  </div>
                  <h3 className="text-xl font-bold text-[#0A2540]" style={{fontFamily:'Syne,sans-serif'}}>
                    Datos de tu operación
                  </h3>
                </div>

                <div className="space-y-5">
                  {/* Industria */}
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Industria</label>
                    <select
                      value={formData.industria}
                      onChange={(e) => setFormData({...formData, industria: e.target.value})}
                      className="w-full px-4 py-3 text-sm bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent"
                    >
                      <option value="retail">Retail / Supermercados</option>
                      <option value="horeca">Gastronomía / HORECA</option>
                      <option value="banca">Banca / Servicios</option>
                      <option value="salud">Salud / Clínicas</option>
                      <option value="otros">Otros</option>
                    </select>
                  </div>

                  {/* Sucursales */}
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Número de sucursales</label>
                    <input
                      type="number"
                      value={formData.sucursales}
                      onChange={(e) => setFormData({...formData, sucursales: e.target.value})}
                      placeholder="Ej: 12"
                      className="w-full px-4 py-3 text-sm bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent"
                    />
                  </div>

                  {/* Ventas mensuales */}
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Ventas mensuales aprox. (CLP millones)</label>
                    <input
                      type="number"
                      value={formData.ventasMensuales}
                      onChange={(e) => setFormData({...formData, ventasMensuales: e.target.value})}
                      placeholder="Ej: 800"
                      className="w-full px-4 py-3 text-sm bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent"
                    />
                  </div>

                  {/* Dispositivos */}
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Número de equipos a implementar</label>
                    <input
                      type="number"
                      value={formData.dispositivos}
                      onChange={(e) => setFormData({...formData, dispositivos: e.target.value})}
                      placeholder="Ej: 8"
                      className="w-full px-4 py-3 text-sm bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent"
                    />
                  </div>

                  {/* Tipo de solución */}
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Tipo de solución</label>
                    <select
                      value={formData.solucion}
                      onChange={(e) => setFormData({...formData, solucion: e.target.value})}
                      className="w-full px-4 py-3 text-sm bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent"
                    >
                      <option value="autoservicio">Kioscos Autoservicio / Self-Checkout</option>
                      <option value="esl">ESL / Precios Digitales</option>
                      <option value="balanzas">Balanzas con IA</option>
                      <option value="pos">Puntos de Venta (POS)</option>
                    </select>
                  </div>

                  {/* Button */}
                  <button
                    onClick={handleCalculate}
                    className="w-full flex items-center justify-center gap-2 px-6 py-4 text-sm font-semibold text-white bg-gradient-to-r from-[#0EA5E9] to-[#0191B4] rounded-xl hover:shadow-xl transition-all"
                  >
                    <Calculator className="w-4 h-4" /> Calcular ROI con IA
                  </button>
                </div>
              </div>
            </motion.div>

            {/* Right: Result */}
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5, delay: 0.2 }}>
              {resultado ? (
                <div className="p-8 rounded-2xl bg-gradient-to-br from-[#0EA5E9]/5 to-[#0191B4]/5 border border-[#0EA5E9]/20">
                  <div className="flex items-center gap-2 mb-6">
                    <CheckCircle className="w-6 h-6 text-emerald-500" strokeWidth={2} />
                    <h4 className="text-lg font-bold text-[#0A2540]" style={{fontFamily:'Syne,sans-serif'}}>
                      Análisis completado
                    </h4>
                  </div>

                  {/* Key Metrics */}
                  <div className="space-y-4 mb-6">
                    <div className="p-5 rounded-xl bg-white border border-slate-200">
                      <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">Inversión Estimada</div>
                      <div className="text-2xl font-bold text-slate-900" style={{fontFamily:'Syne,sans-serif'}}>
                        ${(resultado.inversion / 1000000).toFixed(1)}M
                      </div>
                      <div className="text-xs text-slate-500 mt-1">Para {resultado.dispositivos} equipos en {resultado.sucursales} sucursales</div>
                    </div>

                    <div className="p-5 rounded-xl bg-white border border-slate-200">
                      <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">Ahorro Anual Proyectado</div>
                      <div className="text-2xl font-bold text-emerald-600" style={{fontFamily:'Syne,sans-serif'}}>
                        ${(resultado.ahorroAnual / 1000000).toFixed(1)}M
                      </div>
                      <div className="text-xs text-slate-500 mt-1">{resultado.descripcion}</div>
                    </div>

                    <div className="p-5 rounded-xl bg-gradient-to-br from-[#0EA5E9] to-[#0191B4] text-white">
                      <div className="text-xs uppercase tracking-wider mb-1 opacity-90">ROI Proyectado</div>
                      <div className="text-3xl font-bold" style={{fontFamily:'Syne,sans-serif'}}>
                        {resultado.roi}%
                      </div>
                      <div className="text-xs mt-1 opacity-90">Retorno sobre inversión anual</div>
                    </div>

                    <div className="p-5 rounded-xl bg-[#F8D90F]/10 border border-[#F8D90F]/30">
                      <div className="text-xs text-slate-700 uppercase tracking-wider mb-1 font-semibold">Payback Period</div>
                      <div className="text-2xl font-bold text-[#8B9A00]" style={{fontFamily:'Syne,sans-serif'}}>
                        {resultado.paybackMeses} meses
                      </div>
                      <div className="text-xs text-slate-600 mt-1">Tiempo de recuperación de inversión</div>
                    </div>
                  </div>

                  {/* Visualización simple */}
                  <div className="p-5 rounded-xl bg-white border border-slate-200 mb-6">
                    <div className="text-xs font-semibold text-slate-700 mb-3 uppercase tracking-wider">Proyección 36 Meses</div>
                    <div className="space-y-2">
                      {[
                        { label: 'Año 1', value: resultado.ahorroAnual - resultado.inversion, color: resultado.ahorroAnual > resultado.inversion ? '#10B981' : '#EF4444' },
                        { label: 'Año 2', value: resultado.ahorroAnual, color: '#10B981' },
                        { label: 'Año 3', value: resultado.ahorroAnual, color: '#10B981' },
                      ].map((year, i) => {
                        const isPositive = year.value > 0;
                        const barWidth = Math.min(Math.abs(year.value) / resultado.ahorroAnual * 100, 100);
                        return (
                          <div key={i}>
                            <div className="flex items-center justify-between text-xs mb-1">
                              <span className="font-medium text-slate-600">{year.label}</span>
                              <span className="font-bold" style={{color: year.color}}>
                                {isPositive ? '+' : ''}{(year.value / 1000000).toFixed(1)}M
                              </span>
                            </div>
                            <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                              <div 
                                className="h-full rounded-full transition-all duration-1000"
                                style={{
                                  width: `${barWidth}%`,
                                  backgroundColor: year.color
                                }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div className="text-xs text-slate-500 mt-4 text-center">
                      Beneficio acumulado 3 años: <span className="font-bold text-emerald-600">${((resultado.ahorroAnual * 3 - resultado.inversion) / 1000000).toFixed(1)}M</span>
                    </div>
                  </div>

                  {/* CTA */}
                  <div className="flex flex-col gap-3">
                    <button 
                      onClick={handleDownloadPDF}
                      className="flex items-center justify-center gap-2 px-6 py-3 text-sm font-semibold text-white bg-[#0EA5E9] rounded-xl hover:bg-sky-600 shadow-lg transition-all"
                      data-testid="roi-download-pdf-btn"
                    >
                      <Download className="w-4 h-4" /> Descargar análisis PDF
                    </button>
                    <a href="/contacto" className="flex items-center justify-center gap-2 px-6 py-3 text-sm font-semibold text-[#0EA5E9] border border-[#0EA5E9]/30 rounded-xl hover:bg-[#0EA5E9]/5 transition-all">
                      Agendar reunión con especialista <ArrowRight className="w-4 h-4" />
                    </a>
                  </div>

                  {/* Email PDF form */}
                  <form onSubmit={handleEmailPDF} className="mt-5 p-4 rounded-2xl bg-gradient-to-br from-sky-50 to-white border border-sky-100" data-testid="roi-email-form">
                    <div className="flex items-center gap-2 mb-2">
                      <Mail className="w-4 h-4 text-[#0EA5E9]" />
                      <h5 className="text-sm font-semibold text-[#0A2540]">Recibe este análisis por correo</h5>
                    </div>
                    <p className="text-[11px] text-slate-500 mb-3 leading-relaxed">Te enviamos el PDF y un resumen ejecutivo. Sin spam, solo tu análisis.</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
                      <input required value={sendForm.name} onChange={(e)=>setSendForm({...sendForm, name:e.target.value})} placeholder="Nombre *" className="px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0EA5E9]/30 text-slate-900" data-testid="roi-email-input-name" />
                      <input required type="email" value={sendForm.email} onChange={(e)=>setSendForm({...sendForm, email:e.target.value})} placeholder="Email *" className="px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0EA5E9]/30 text-slate-900" data-testid="roi-email-input-email" />
                      <input value={sendForm.company} onChange={(e)=>setSendForm({...sendForm, company:e.target.value})} placeholder="Empresa" className="px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0EA5E9]/30 text-slate-900" />
                      <input value={sendForm.phone} onChange={(e)=>setSendForm({...sendForm, phone:e.target.value})} placeholder="Teléfono" className="px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0EA5E9]/30 text-slate-900" />
                    </div>
                    <button type="submit" disabled={sending} className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold text-white bg-[#0A2540] rounded-lg hover:bg-slate-800 disabled:opacity-50 transition-all" data-testid="roi-email-submit">
                      {sending ? <><Loader2 className="w-4 h-4 animate-spin" /> Enviando…</> : <><Mail className="w-4 h-4" /> Enviarme el PDF</>}
                    </button>
                  </form>

                  <p className="text-xs text-slate-500 text-center mt-4">
                    * Cálculos estimados basados en proyectos similares. Análisis detallado en reunión personalizada.
                  </p>
                </div>
              ) : (
                <div className="h-full flex items-center justify-center p-12 rounded-2xl bg-slate-50 border border-slate-200">
                  <div className="text-center">
                    <Zap className="w-16 h-16 mx-auto mb-4 text-slate-300" strokeWidth={1.5} />
                    <h4 className="text-lg font-semibold text-slate-400 mb-2">Ingresa tus datos</h4>
                    <p className="text-sm text-slate-400">
                      Completa el formulario y te mostramos<br/>tu ROI proyectado instantáneamente
                    </p>
                  </div>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </Section>

      {/* Benefits */}
      <Section alt>
        <SectionTitle 
          badge="POR QUÉ USAR ESTA CALCULADORA" 
          title="Proyecciones basadas en datos reales" 
          center 
        />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-5xl mx-auto">
          {[
            { icon: TrendingUp, title: 'Datos verificables', desc: '469 proyectos ejecutados desde 2003' },
            { icon: DollarSign, title: 'Fórmulas reales', desc: 'Basado en ahorros medidos en terreno' },
            { icon: Zap, title: 'Instantáneo', desc: 'Resultado en menos de 10 segundos' },
            { icon: CheckCircle, title: 'Sin compromiso', desc: 'Explora sin presión comercial' },
          ].map((benefit, i) => {
            const Icon = benefit.icon;
            return (
              <motion.div 
                key={i}
                variants={fadeUp}
                className="p-5 rounded-xl bg-white border border-slate-200 shadow-sm text-center"
              >
                <Icon className="w-8 h-8 mx-auto mb-3 text-[#0191B4]" strokeWidth={1.5} />
                <h5 className="text-sm font-semibold text-[#0A2540] mb-1">{benefit.title}</h5>
                <p className="text-xs text-slate-500 leading-relaxed">{benefit.desc}</p>
              </motion.div>
            );
          })}
        </div>
      </Section>
    </div>
  );
}
